// Copyright (c) StrangeBee 2023

import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { useLocalMessage } from '@Helpers/locale';
import FieldsetSelected from '@Molecules/FieldsetSelected/FieldsetSelected';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { THForm } from '@Molecules/THForm/THForm';
import React, { ReactElement, useMemo, useState } from 'react';
import { AnalyzerOutput, ObservableOutput } from 'thehive-sdk';
import { computeDataSelected, parseTokenAnalyzer } from './helper';

interface AnalyzerDrawerProps {
    items?: ObservableOutput[];
    analyzer?: AnalyzerOutput[];
    triggerElem: ReactElement;
    onFinish?: (...args: any[]) => any;
}

const AnalyzerDrawer = ({ items, triggerElem, analyzer, onFinish }: AnalyzerDrawerProps): ReactElement => {
    const i = useLocalMessage();

    const [paramsJobs, setParamsJobs] = useState<any[]>([]);

    const dataObservableSelected: Record<string, any> = useMemo(
        () => computeDataSelected(items, analyzer),
        [items, analyzer],
    );

    const reset = (): void => {
        setParamsJobs([]);
    };

    const onClickHandler = (toggleDrawer: (...args: any[]) => any) => (): void => {
        const treatedParams = paramsJobs.reduce((acc, { value }) => {
            const { observableIds, analyzerId, cortexId } = parseTokenAnalyzer(value);
            let params: any[] = [];
            observableIds.forEach((observableId: string) => {
                params = [...params, { observableId, analyzerId, cortexId }];
            });

            return [...acc, ...params];
        }, []);

        if (treatedParams.length > 0 && onFinish) {
            onFinish?.(treatedParams);
        }

        toggleDrawer();
        reset();
    };

    return (
        <THDrawer
            title={i('common.analyzer')}
            triggerElem={triggerElem}
            onClose={(): void => reset()}
            className="drawer-form"
            disableClose={paramsJobs.length != 0}
        >
            {(toggleDrawer) => (
                <THForm onFinish={onClickHandler(toggleDrawer)}>
                    <THForm.Layout
                        footer={
                            <FormSubmitButton
                                onCancel={() => {
                                    toggleDrawer();
                                    reset();
                                }}
                                disabled={paramsJobs.length <= 0}
                                footerText={i('analyzer.drawer.run')}
                            />
                        }
                    >
                        <THForm.Item>
                            {Object.entries(dataObservableSelected).map(([type, { analyzers }]) => (
                                <FieldsetSelected
                                    key={type}
                                    title={`${type} analyzer`}
                                    data={analyzers}
                                    selectedState={[paramsJobs, setParamsJobs]}
                                />
                            ))}
                        </THForm.Item>
                    </THForm.Layout>
                </THForm>
            )}
        </THDrawer>
    );
};
export default AnalyzerDrawer;
